// ============================================================================
// WorkVerificationStep.jsx - Verify Work Scope with Customer
// ============================================================================
// Purpose: Display services to be performed and get customer confirmation
// ============================================================================

import React, { Component } from 'react';

class WorkVerificationStep extends Component {
  constructor(props) {
    super(props);

    // Retail jobs default to NOT including CarFax (opt-out); dealer jobs default
    // to included. The installer can toggle it per-vehicle before confirming.
    const isRetail = props.request?.Request_type === 'retail';

    this.state = {
      error: null,
      customerConfirmed: false,
      carfaxIncluded: !isRetail,
      showCarfaxBenefits: false
    };

    this.handleConfirm = this.handleConfirm.bind(this);
  }

  handleConfirm() {
    this.setState({ customerConfirmed: true });

    // carfax_opt_out is true when checkbox is unchecked
    const carfaxOptOut = this.props.carfaxInfo ? !this.state.carfaxIncluded : false;

    // Notify parent component
    this.props.onWorkVerified(carfaxOptOut);
  }

  calculateTotal() {
    const services = this.props.services || [];
    return services.reduce((total, service) => {
      const price = parseFloat(service.service_amount || service.service_cost || 0);
      return total + price;
    }, 0);
  }

  render() {
    const services = this.props.services || [];
    const total = this.calculateTotal();

    // Determine step number based on whether signature was required
    // If checkin has requires_signature, this is step 4, otherwise step 3
    const stepNumber = this.props.request?.Request_type === 'retail' ? 4 : 3;

    return (
      <div className="work-verification-step">
        <h4 className="text-center mb-4">Step {stepNumber}: Confirm Work Scope</h4>

        {/* Error Display */}
        {this.state.error && (
          <div className="alert alert-danger" role="alert">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            {this.state.error}
          </div>
        )}

        {/* Vehicle Info */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-10">
            <div className="card">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <i className="fa-sharp fa-regular fa-car mr-2"></i>
                  Vehicle Information
                </h5>
              </div>
              <div className="card-body">
                <p className="mb-1">
                  <strong>Vehicle:</strong> {this.props.request?.vehicleYear} {this.props.request?.vehicleMake} {this.props.request?.vehicleModel}
                  {this.props.request?.vehicleTrim && ` ${this.props.request.vehicleTrim}`}
                </p>
                {this.props.request?.vin && !this.props.request.vin.toUpperCase().startsWith('NV') && (
                  <p className="mb-0">
                    <strong>VIN:</strong> {this.props.request.vin}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Services List */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-10">
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa-sharp fa-regular fa-wrench mr-2"></i>
                  Services to be Performed
                </h5>
              </div>
              <div className="card-body p-0">
                {services.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                    No services found for this request
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Service</th>
                          <th className="text-center">Qty</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((service, index) => {
                          const qty = service.quantity || 1;
                          const unitPrice = parseFloat(service.unit_price || service.service_cost || 0);
                          const lineTotal = parseFloat(service.service_amount || service.service_cost || 0);

                          return (
                            <tr key={index}>
                              <td>
                                <strong>
                                  {service.service_category && `${service.service_category} - `}
                                  {service.custom_description || service.service_name || service.description}
                                </strong>
                              </td>
                              <td className="text-center">{qty}</td>
                              <td className="text-right">${unitPrice.toFixed(2)}</td>
                              <td className="text-right">
                                <strong>${lineTotal.toFixed(2)}</strong>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-light">
                        <tr>
                          <td colSpan="3" className="text-right">
                            <strong>Total:</strong>
                          </td>
                          <td className="text-right">
                            <h5 className="mb-0 text-primary">
                              <strong>${total.toFixed(2)}</strong>
                            </h5>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Agreement */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-10">
            <div className="alert alert-info">
              <h6 className="mb-2">
                <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                <strong>Customer Acknowledgment</strong>
              </h6>
              <p className="mb-0">
                I confirm that I have reviewed the services listed above and authorize
                the work to be performed for the total amount of <strong>${total.toFixed(2)}</strong>.
                I understand that additional charges may apply if unforeseen issues are discovered
                during service, and I will be contacted for approval before any additional work is performed.
              </p>
            </div>
          </div>
        </div>

        {/* CarFax Opt-Out */}
        {this.props.carfaxInfo && (
          <div className="row justify-content-center mb-4">
            <div className="col-md-10">
              <div className="border rounded p-3 bg-light">
                <div className="d-flex align-items-center">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="carfax-include"
                      checked={this.state.carfaxIncluded}
                      onChange={(e) => this.setState({ carfaxIncluded: e.target.checked })}
                      disabled={this.props.loading}
                    />
                    <label className="custom-control-label" htmlFor="carfax-include">
                      Include this service on the vehicle's CarFax report
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ml-2"
                    onClick={() => this.setState({ showCarfaxBenefits: !this.state.showCarfaxBenefits })}
                  >
                    <i className="fa-sharp fa-regular fa-info-circle text-primary"></i>
                  </button>
                </div>
                {this.state.showCarfaxBenefits && this.props.carfaxInfo.benefits && (
                  <div className="mt-3 pl-4">
                    <strong className="d-block mb-1">Why include on CarFax?</strong>
                    <ul className="mb-0 pl-3">
                      {this.props.carfaxInfo.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Confirm Button */}
        <div className="row">
          <div className="col-12 text-center">
            <button
              className="btn btn-primary btn-lg"
              onClick={this.handleConfirm}
              disabled={services.length === 0 || this.state.customerConfirmed || this.props.loading}
            >
              {this.props.loading && (
                <span className="mr-2">
                  <i className="fa-sharp fa-regular fa-refresh fa-spin" />
                </span>
              )}
              <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
              Complete Check-In
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default WorkVerificationStep;
